import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { Logger } from 'aws-amplify';

import { 
  makeStyles,
  useTheme,
} from '@material-ui/core/styles';

import {
  Collapse,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
} from '@material-ui/core';

import {
  ExpandMore as ExpandMoreIcon
} from '@material-ui/icons';

const logger = new Logger('DetailPanel');

const useStyles = makeStyles((theme) => ({
  root: {
    //display: 'flex',
    //flexFlow: 'column',
    //backgroundColor: theme.palette.background.paper,
    //width: '100%',
  },
  field: {
    display: 'flex',
    flexFlow: 'column',
    //minWidth: 100,
    //maxWidth: 300,
  },
  label: {
    // minWidth: 100,
    // maxWidth: 100,
    [theme.breakpoints.up("sm")]: {    
      textAlign: 'right',
    }
  },
  expand: {
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
}));

const getFieldValue = (data, field) => {
  try {
    let value=data;
    const elems=field.split('.');
    for(let i=0;i<elems.length;i++) {
      const elem=elems[i];
      if(elem==='$') {
        continue;
        
      // element is an array type with inner field ref, recurse into array elements
      } else if(elem.indexOf('[')>=0) {
        const elemParts=elem.split(/\[(.+)/);
        const elemRoot=elemParts[0];
        const elemInnerField=elemParts[1].substring(0, elemParts[1].length - 1);
        if(value.hasOwnProperty(elemRoot)) {
          value=value[elemRoot];
          if(Array.isArray(value)) {
            const values=[];
            for(let j=0;j<value.length;j++) {
              values.push(getFieldValue(value[j], elemInnerField));
            }
            return values;
          } else {
            logger.warn(`element '${elem}' of field '${field}' requires an array type!`);
          }
        }
        
      } else if(value.hasOwnProperty(elem)) {
        value=value[elem];
      } else {
        value="";
        return;
      }
    }
    return value;
  } catch(e) {
    logger.debug(`field '${field}' not found in data: `, e);
    return(null);
  }
};

export default function DetailPanel(props) {
  const { 
    name, data, fields, 
    gridSpacing, expandable, footer, expand, onExpandClick,
    labelWidth, fieldWidth, panelFullWidth
  } = props;
  const [expanded, setExpanded] = React.useState(props.expanded || !expandable);

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('xs'));

  const labelVariant='caption';
  const fieldVariant='body2';
  const classes = useStyles();

  const handleExpandClick = () => {
    if(onExpandClick) {
      onExpandClick(!expanded);
    }
    setExpanded(!expanded);
  };

  // expand when triggered from parent after initial render
  React.useEffect(() => {
      if(expand>0) { setExpanded(true); }
    }, [expand]
  );

  const FooterComponent=footer.component;
  
  return (
    <div className={classes.root} style={{ width: (!expanded || panelFullWidth)?'calc(100vw - 60px)':null}}>
      { name.length ? <Typography variant="overline">{name}</Typography> : null }
      { expandable ? <IconButton
          className={clsx(classes.expand, {
            [classes.expandOpen]: expanded,
          })}
          onClick={handleExpandClick}
          aria-expanded={expanded}
          aria-label="show more"
          size="small"
        >
          <ExpandMoreIcon />
        </IconButton>:null
      }
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {
          fields.map((field, index) => {
            const dataRef=field.data?field.data:field.name;
            const value=getFieldValue(data,dataRef);
            const formatter=field.formatter;
            const formattedValue=formatter?formatter(value):value;
            const FieldComponent=field.component;
            const isRenderable=(FieldComponent || !(typeof formattedValue === 'object' && formattedValue !== null));
            if(!isRenderable) {
              var msg=`unable to render data '${field.data}' for field '${field.label}' in the '${name}' panel as it appears to be a raw object`;
              if(!(field.formatter || field.component)) {
                msg+="... use a 'component' or 'formatter' to make it renderable";
              }
              logger.warn(`${msg}: `,formattedValue);
            }
            let maxFieldWidth=fieldWidth?fieldWidth:'100%';
            if ( field.label !== '_' && labelWidth && !isSmall) {
              maxFieldWidth=`calc(${maxFieldWidth} - ${labelWidth})`;
            }
            const fieldProps={
              variant: fieldVariant,
            };
            fieldProps[field.valueProp || 'value']=formattedValue;
            return(
              <Grid container spacing={gridSpacing} key={index} style={{ alignItems: field.labelAlign?field.labelAlign:FieldComponent?"center":"baseline"}}>
                { field.label !== '_' ?
                <Grid item xs={12} sm className={classes.label} style={{ minWidth: labelWidth, maxWidth: labelWidth}}>
                  <Typography variant={labelVariant} color="textSecondary">
                    {field.label?field.label:field.name}
                  </Typography>
                </Grid> : null}
                <Grid item xs={12} container sm className={classes.field} 
                  style={{ maxWidth: maxFieldWidth }}>
                  {FieldComponent?(
                    <FieldComponent {...fieldProps} {...field.options}/>
                  ) : (
                    <Typography variant={fieldVariant} color="textPrimary">
                      {isRenderable?formattedValue:null}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            );
          })
        }
        { footer.component ? <FooterComponent {...footer.props} /> : null }
      </Collapse>
    </div>
  );
}

DetailPanel.propTypes = {
  name: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  fields: PropTypes.array.isRequired,
  gridSpacing: PropTypes.number.isRequired,
  expandable: PropTypes.bool.isRequired,
  expanded: PropTypes.bool.isRequired,
  footer: PropTypes.object.isRequired,
  expand: PropTypes.number,
  onExpandClick: PropTypes.func,
  labelWidth: PropTypes.string,
  fieldWidth: PropTypes.string,
  panelFullWidth: PropTypes.bool,
};

DetailPanel.defaultProps = {
  name: "",
  data: {},
  fields: [],
  gridSpacing: 1,
  expandable: false,
  expanded: true,
  footer: {},
  labelWidth: '100px',
  fieldWidth: '300px',
  panelFullWidth: false,
};
